/**
 * Reduced motion for the juice kit. Every effect has a still version, chosen here, so no
 * component asks the media query itself.
 */
import { useEffect, useState } from 'preact/hooks';

export const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

/** True when the player has asked for less motion. False where there is no window. */
export function prefersReducedMotion(): boolean {
  try {
    return typeof matchMedia === 'function' && matchMedia(REDUCED_MOTION_QUERY).matches;
  } catch {
    return false;
  }
}

/** The same, live. */
export function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(prefersReducedMotion);
  useEffect(() => {
    let mq: MediaQueryList;
    try {
      mq = matchMedia(REDUCED_MOTION_QUERY);
    } catch {
      return;
    }
    const onChange = () => setReduced(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return reduced;
}

/** A point on the screen, in viewport pixels. */
export interface Point {
  x: number;
  y: number;
}

/** The centre of an element, in viewport pixels. */
export function centreOf(el: Element): Point {
  const r = el.getBoundingClientRect();
  return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
}

/** The centre of the element marked `data-coin-target`, the coin counter, if one is drawn. */
export function coinTarget(): Point | null {
  if (typeof document === 'undefined') return null;
  const el = document.querySelector('[data-coin-target]');
  return el ? centreOf(el) : null;
}

/**
 * How far a bar from `start` lasting `duration` has filled at `now`, from 0 to 1. A bar with
 * no length is full.
 */
export function fillAt(start: number, duration: number, now: number): number {
  if (!(duration > 0)) return 1;
  return Math.max(0, Math.min(1, (now - start) / duration));
}

/**
 * The fill of a progress bar. In motion it is a CSS animation started part way through, so
 * nothing re-renders each frame; still, it is the fill at `now` and moves only when the
 * parent draws again.
 */
export function fillStyle(
  start: number,
  duration: number,
  now: number,
  reduced: boolean,
  since: number = now,
): Record<string, string> {
  const done = fillAt(start, duration, now);
  if (reduced || done >= 1 || duration <= 0) return { transform: `scaleX(${done})` };
  // Item 226: the delay is fixed by `since`, when this animation was first drawn. A browser
  // applies a changed delay to a running animation as if it had it from the start, so a delay
  // worked out from each redraw's `now` pushed the bar ahead: full while the crop still grew.
  return {
    transform: 'scaleX(0)',
    animation: `juice-fill ${Math.round(duration)}ms linear ${-Math.round(since - start)}ms forwards`,
  };
}

/** A small seeded spread, so confetti and coins fall the same way each render. */
export function spread(i: number, seed = 1): number {
  const x = Math.sin((i + 1) * 12.9898 * seed) * 43758.5453;
  return x - Math.floor(x);
}
